import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu, Bell, Copy, Check, User, LogOut,
  ChevronDown, AlertTriangle, Loader2,
} from 'lucide-react';
import { authAPI } from '../../services/api';

/* ── design tokens (inline — topbar is standalone) ── */
const C = {
  surface:     '#ffffff',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primaryDark: '#6d28d9',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      10,
};

const initials = (s) => (s || 'A').trim().slice(0, 2).toUpperCase();

/* ── MID copy badge ── */
const MidBadge = ({ mid }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!mid) return;
    navigator.clipboard.writeText(mid);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button onClick={onCopy} title="Merchant ID — click to copy"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 12px', borderRadius: 999,
        background: C.primarySoft, color: C.primary,
        border: `1px solid ${C.primaryTint}`,
        fontSize: 12, fontWeight: 600,
        fontFamily: 'Menlo, Consolas, monospace',
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontFamily: 'Inter,system-ui,sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', opacity: .6 }}>
        MID
      </span>
      <span>{mid}</span>
      {copied ? <Check size={11} style={{ color: '#10B981' }} /> : <Copy size={11} style={{ opacity: .55 }} />}
    </button>
  );
};

/* ── logout confirmation modal ── */
const LogoutModal = ({ onConfirm, onCancel, loading }) => (
  <div
    onClick={onCancel}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, backdropFilter: 'blur(3px)',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: C.surface, borderRadius: C.radius + 2,
        width: '100%', maxWidth: 360, padding: '28px 28px 22px',
        boxShadow: '0 20px 60px rgba(20,20,43,.2)',
        textAlign: 'center',
      }}
    >
      {/* icon */}
      <div style={{
        width: 52, height: 52, borderRadius: '50%',
        background: '#FEE2E2', color: '#DC2626',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 18px',
      }}>
        <LogOut size={22} strokeWidth={1.8} />
      </div>

      <h3 style={{ fontSize: 17, fontWeight: 800, color: C.ink, margin: '0 0 8px' }}>
        Sign out?
      </h3>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 24px', lineHeight: 1.5 }}>
        You'll need to sign in again to access your account.
      </p>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancel} disabled={loading}
          style={{
            flex: 1, padding: '10px 0', borderRadius: C.radius,
            background: C.borderSoft, color: C.body,
            border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}>
          Cancel
        </button>
        <button onClick={onConfirm} disabled={loading}
          style={{
            flex: 1, padding: '10px 0', borderRadius: C.radius,
            background: loading ? C.muted : C.danger,
            color: '#fff', border: 'none',
            fontWeight: 700, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
          }}>
          {loading
            ? <><Loader2 size={13} style={{ animation: 'spin .8s linear infinite' }} /> Signing out…</>
            : <><LogOut size={13} strokeWidth={2} /> Sign Out</>
          }
        </button>
      </div>
    </div>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

/* ── profile dropdown ── */
const ProfileDropdown = ({ user, role, onLogoutClick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const profilePath = role === 'admin' ? '/admin/profile' : '/user/profile';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '5px 10px 5px 5px',
          borderRadius: C.radius, border: `1px solid ${open ? C.primaryTint : C.border}`,
          background: open ? C.primarySoft : C.surface,
          cursor: 'pointer', transition: 'all .13s',
        }}
      >
        {/* avatar */}
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: C.primary, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800, letterSpacing: '.04em', flexShrink: 0,
        }}>
          {initials(user?.username || user?.email)}
        </div>
        {/* name */}
        <div style={{ textAlign: 'left', lineHeight: 1.25 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.username || user?.email || (role === 'admin' ? 'Admin' : 'Merchant')}
          </div>
          <div style={{ fontSize: 10, color: C.muted, textTransform: 'capitalize' }}>{role}</div>
        </div>
        <ChevronDown size={12} color={C.muted}
          style={{ transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>

      {/* dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: C.radius, minWidth: 200, zIndex: 999,
          boxShadow: '0 8px 32px rgba(20,20,43,.12)',
          overflow: 'hidden',
        }}>
          {/* user info header */}
          <div style={{
            padding: '14px 16px 12px',
            borderBottom: `1px solid ${C.borderSoft}`,
            background: C.primarySoft,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>
              {user?.username || user?.email || '—'}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {user?.email}
            </div>
          </div>

          {/* menu items */}
          <div style={{ padding: '6px 0' }}>
            <Link
              to={profilePath}
              onClick={() => setOpen(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 16px', fontSize: 13, fontWeight: 600,
                color: C.body, textDecoration: 'none',
                transition: 'background .1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = C.borderSoft}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: 28, height: 28, borderRadius: 8, background: C.primarySoft, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <User size={13} strokeWidth={2} />
              </div>
              My Profile
            </Link>

            <div style={{ margin: '4px 12px', borderTop: `1px solid ${C.borderSoft}` }} />

            <button
              onClick={() => { setOpen(false); onLogoutClick(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 16px', fontSize: 13, fontWeight: 600,
                color: '#DC2626', background: 'none',
                border: 'none', width: '100%', cursor: 'pointer',
                transition: 'background .1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: 28, height: 28, borderRadius: 8, background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LogOut size={13} strokeWidth={2} />
              </div>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════
   PanelTopbar
══════════════════════════════════════════════════ */
const PanelTopbar = ({ title, breadcrumb, actions, user, role, onMobileToggle }) => {
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut,      setLoggingOut]      = useState(false);

  const confirmLogout = async () => {
    setLoggingOut(true);
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <>
      <header className="panel-topbar">
        {/* ── Left: mobile toggle + breadcrumb/title ── */}
        <div className="panel-topbar__title">
          <button
            className="panel-topbar__icon-btn"
            onClick={onMobileToggle}
            title="Toggle menu"
            aria-label="Toggle menu"
            id="pn-mobile-toggle"
            style={{ display: 'none' }}
          >
            <Menu size={15} strokeWidth={1.8} />
          </button>

          {breadcrumb && (
            <>
              <span className="panel-topbar__crumb">{breadcrumb}</span>
              <span className="panel-topbar__crumb-sep">/</span>
            </>
          )}
          {title && <span className="panel-topbar__page-title">{title}</span>}
        </div>

        {/* ── Right: actions + MID + notifications + profile dropdown ── */}
        <div className="panel-topbar__actions">
          {actions}

          {role !== 'admin' && user?.mid && <MidBadge mid={user.mid} />}

          <div className="panel-topbar__divider" />

          {/* notifications */}
          <button className="panel-topbar__icon-btn" title="Notifications">
            <Bell size={15} strokeWidth={1.8} />
          </button>

          {/* profile dropdown */}
          <ProfileDropdown
            user={user}
            role={role}
            onLogoutClick={() => setShowLogoutModal(true)}
          />
        </div>
      </header>

      {/* ── Logout confirmation modal ── */}
      {showLogoutModal && (
        <LogoutModal
          loading={loggingOut}
          onConfirm={confirmLogout}
          onCancel={() => setShowLogoutModal(false)}
        />
      )}

      <style>{`
        @media (max-width: 880px) {
          #pn-mobile-toggle { display: inline-flex !important; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default PanelTopbar;
